import { replace as replacePath } from 'react-router-redux';
import { change } from 'redux-form';
import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { all, call, put, select, spawn } from 'redux-saga/effects';
import { msaOfferById, planById } from 'redux/saga/entities/bound';
import { friendbuyCode } from 'redux/saga/selectors';
import { createUserAndLogIn } from 'features/Login/Authentication.saga';
import { signup } from 'features/Signup/FormAction';
import { msaIdToAlias } from 'constants/msas';
import { ga } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { getCorporateDetails } from 'helpers/corporate';
import { trackGoogleTagManagerEvent } from 'components/GoogleTagManager/GoogleTagManager';
import { addCartItem, emptyCart } from 'redux/modules/cart';
import { BaseProduct } from './BaseProduct';

const REFERRAL_PROMOTION_ALIASES = ['referafriend', 'referral-trial'];

export class MsaOfferProduct extends BaseProduct {
  static key = 'MsaOfferProduct';

  static *loadOffer(client, msaOfferId) {
    try {
      return yield call(fetchEntityIfNeeded, msaOfferById, [client, msaOfferId]);
    } catch (err) {
      if (err.code === 404) {
        yield put(replacePath('/'));
        return;
      }
      throw err;
    }
  }

  static *loadSignupData(client) {
    const { msaOfferId } = yield call(MsaOfferProduct.getProductData);
    const msaOffer = yield call(MsaOfferProduct.loadOffer, client, msaOfferId);

    if (msaOffer) {
      const msaId = msaOffer.msa.id;

      yield call(segment.track, 'Membership Purchase', {
        msaId,
        msaName: msaIdToAlias(msaId),
        gtm: true,
      });

      yield spawn(trackGoogleTagManagerEvent, {
        msaId,
        msaName: msaIdToAlias(msaId),
      });

      yield put(change('signup', 'msaOfferId', msaOfferId));
    }
  }

  static *submitSignupData(client, basePayload) {
    const { msaOfferId } = yield call(MsaOfferProduct.getProductData);
    return yield call(createUserAndLogIn, client, signup, {
      ...basePayload,
      msa_offer_id: msaOfferId,
    });
  }

  static *loadPurchaseData(client) {
    const { msaOfferId } = yield call(MsaOfferProduct.getProductData);
    const msaOffer = yield call(MsaOfferProduct.loadOffer, client, msaOfferId);

    if (msaOffer) {
      const referAFriendCode = REFERRAL_PROMOTION_ALIASES.includes(msaOffer.promotion.alias)
        ? yield select(friendbuyCode)
        : '';
      const corporateDetails = yield call(getCorporateDetails, msaOffer.initial_plan.plan_code);

      yield all([
        call(fetchEntityIfNeeded, planById, [client, msaOffer.initial_plan.id]),
        put(change('purchase', 'msaOfferId', msaOfferId)),
        put(change('purchase', 'msaId', msaOffer.msa.id)),
        put(change('purchase', 'planId', msaOffer.initial_plan.id)),
        put(change('purchase', 'friendbuyCode', referAFriendCode)),
        put(change('purchase', 'corporateDetails', corporateDetails)),
      ]);

      yield all([
        put(emptyCart()),
        put(
          addCartItem('plan', msaOffer.content.purchase_info.charge_name, msaOffer.price, {
            planCode: msaOffer.initial_plan.plan_code,
            // JIRA Ticket EXPANSION-4135: remove "+tax" in new user signup flow.
            // When a new user sign up and purchase a plan, we fetch plan data from
            // end point _api/promotion/v1/promotions/getclasspass?msa_id={msa_id}
            // The response don't have the information that if the current plan is taxable
            // Please refer to the discussion in this Ticket:
            // https://classpass.atlassian.net/browse/EXPANSION-4085
            // Decision from product is to hide the "+ Tax", so we set isTaxable to false.
            isTaxable: false,
          })
        ),
      ]);
    }
  }

  static *trackPurchaseFailure() {
    const { msaOfferId } = yield call(MsaOfferProduct.getProductData);
    yield call(segment.track, 'Membership Purchase Errors', {
      category: 'Membership Purchase',
      label: `offer-${msaOfferId}`,
    });
    yield spawn(ga, 'Membership Purchase', 'Membership Purchase Errors', `offer-${msaOfferId}`);
  }

  static *trackPurchaseSuccess(result) {
    yield call(BaseProduct.trackPurchaseSuccess, result);
  }

  static *trackSignupFailure() {
    const { msaOfferId } = yield call(MsaOfferProduct.getProductData);
    yield call(segment.track, 'Create Account Error', {
      category: 'Membership Purchase',
      label: `offer-${msaOfferId}`,
    });
    yield spawn(ga, 'Membership Purchase', 'Create Account Error', `offer-${msaOfferId}`);
  }

  static *trackSignupSuccess() {
    const { msaOfferId } = yield call(MsaOfferProduct.getProductData);
    yield call(segment.track, 'Membership Account Creation', {
      category: 'Membership Purchase',
      label: `offer-${msaOfferId}`,
    });
    yield spawn(ga, 'Membership Purchase', 'Membership Account Creation', `offer-${msaOfferId}`);
  }
}
